import React, { useState, useEffect } from 'react';
import { db } from '../../db/shiksha-db';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRightLeft, Save, AlertTriangle, UserCheck, GraduationCap, Archive } from 'lucide-react';
import { getAuth } from 'firebase/auth';

export function AdminRollover() {
  const students = useLiveQuery(() => db.students.toArray()) || [];
  const marks = useLiveQuery(() => db.marks.toArray()) || [];
  
  const [studentStats, setStudentStats] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [newAyName, setNewAyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const auth = getAuth();

  useEffect(() => {
    // Current AY inference
    const currentYear = new Date().getFullYear();
    setNewAyName(`${currentYear}-${currentYear + 1}`);
  }, []);

  useEffect(() => {
    if (!students.length) return;

    // Aggregate marks per student
    const stats = students.map(student => {
      const studentMarks = marks.filter(m => m.studentId === String(student.id) || m.studentId === student.rollNo);
      
      let totalScore = 0;
      let totalMax = 0;
      
      studentMarks.forEach(m => {
        totalScore += Number(m.score) || 0;
        totalMax += Number(m.maxScore) || 0;
      });

      const percentage = totalMax > 0 ? ((totalScore / totalMax) * 100).toFixed(1) : 0;
      
      return {
        ...student,
        percentage,
        marksCount: studentMarks.length,
        // Default to what's already saved, or 'promote' if Class X, maybe 'archive' for Class X later, but user sets it
        currentStatus: student.academicStatus || 'promote'
      };
    });

    // Sort by class then roll no
    stats.sort((a, b) => {
      if (a.class !== b.class) return (a.class || '').localeCompare(b.class || '');
      return (Number(a.rollNo) || 0) - (Number(b.rollNo) || 0);
    });

    setStudentStats(stats);
    
    // Initialize decision state
    const initialDecisions = {};
    stats.forEach(s => {
      initialDecisions[s.id] = s.currentStatus;
    });
    setDecisions(initialDecisions);
    
  }, [students, marks]);

  const handleDecisionChange = async (studentId, status) => {
    setDecisions(prev => ({ ...prev, [studentId]: status }));
    // Save to local IndexedDB immediately
    await db.students.update(studentId, { academicStatus: status });
  };

  const handleExecuteRollover = async () => {
    if (!newAyName) {
      setError('Please provide a name for the new Academic Year.');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      
      const token = await user.getIdToken();
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api';
      
      const response = await fetch(`${apiUrl}/admin/rollover-ay`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newAyName,
          decisions,
          adminEmail: user.email
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to execute rollover on server.');
      }
      
      setSuccess(`Rollover successful. New AY Sheet ID: ${data.aySheetId}`);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ArrowRightLeft className="text-indigo-600" />
            Academic Year Rollover
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Review student final percentages and assign their promotion status before creating the new academic year sheet.
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">New AY Name</label>
            <input
              type="text"
              value={newAyName}
              onChange={(e) => setNewAyName(e.target.value)}
              className="border border-gray-300 rounded-md p-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g. 2024-2025"
            />
          </div>
          <button
            onClick={handleExecuteRollover}
            disabled={isSubmitting || studentStats.length === 0}
            className={`mt-5 flex items-center gap-2 px-4 py-2 rounded-md font-medium text-white transition-colors ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 shadow-sm'
            }`}
          >
            <Save size={18} />
            {isSubmitting ? 'Processing...' : 'Execute Rollover'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start text-red-800">
          <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start text-green-800">
          <UserCheck className="w-5 h-5 mr-2 flex-shrink-0" />
          <p className="text-sm">{success}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Class</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Final %</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rollover Decision</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {studentStats.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{student.name}</div>
                    <div className="text-sm text-gray-500">Roll: {student.rollNo}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.class} {student.section && `- ${student.section}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      Number(student.percentage) >= 40 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {student.percentage}%
                    </span>
                    <div className="text-xs text-gray-400 mt-1">{student.marksCount} entries</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-3">
                      <select
                        value={decisions[student.id] || 'promote'}
                        onChange={(e) => handleDecisionChange(student.id, e.target.value)}
                        className={`block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md font-medium ${
                          decisions[student.id] === 'promote' ? 'text-green-700 bg-green-50' : 
                          decisions[student.id] === 'retain' ? 'text-amber-700 bg-amber-50' : 
                          'text-gray-700 bg-gray-100'
                        }`}
                      >
                        <option value="promote">✅ Promote</option>
                        <option value="retain">🔄 Retain (Same Class)</option>
                        <option value="archive">📦 Archive / Transfer</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
              {studentStats.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    No students found in the local database. Ensure sync is complete.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
